import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { patientsApi, codesApi, claimsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { ArrowLeft, Plus, Trash, Gear, CheckCircle, ArrowRight, MagnifyingGlass, ClockCounterClockwise, ShieldCheck, Info } from '@phosphor-icons/react';
import { logger } from '../lib/logger';
import { countyToCodbTier } from '../constants/ohioCountyCodb';

// P32 (Feb 27, 2026): hoisted constants + utils + components into ./rhinobill-claim/*
import {
  DODD_CODES,
  isDoddCode,
  PLACE_OF_SERVICE_OPTIONS,
  MAX_VISIT_DURATION_HOURS,
  FALLBACK_MODIFIER_CATALOG,
  HCPCS_VARIANTS_FRONTEND,
  ASSESSMENT_CODES_FRONTEND,
} from './rhinobill-claim/constants';
import {
  todayISO, dayOfWeek, formatDateMDY, unitsToHHMM, smartParseDate,
} from './rhinobill-claim/utils/dates';
import {
  alwaysApplyFor, distinctPayerOptions, friendlyPayerDisplay, maxUnitsFor,
  previewModifier, resolveHcpcsFromVariant, servicesForPayer,
} from './rhinobill-claim/utils/hcpcsVariants';
import { newServiceLine } from './rhinobill-claim/utils/serviceLine';
import SmartDateInput from './rhinobill-claim/components/SmartDateInput';
import { ModifierCell, ModifierChip } from './rhinobill-claim/components/ModifierChips';
import PatientAutocomplete from './rhinobill-claim/components/PatientAutocomplete';
import PreviousClaimDialog from './rhinobill-claim/components/PreviousClaimDialog';
import PreviewClaimDialog from './rhinobill-claim/components/PreviewClaimDialog';

/**
 * Rhinobill-style claim form (Feb 22, 2026 — P22 iteration; P32 refactor split).
 *
 * Adds on top of the P20/P21 single-page form:
 *   1. Patient autocomplete (name + Medicaid ID + DOB, type-to-search)
 *   2. Payer dropdown driven by the patient's billing_lines
 *   3. Service dropdown filtered by selected payer (HCPCS codes the
 *      patient is enrolled for under that payer)
 *   4. Patient Responsibility ($) — deducted from total → net payout
 *   5. View/Recycle Previous Claim — side-by-side comparison
 *   6. Settings ⚙️ popover with 3 toggles
 *
 * P32 split tree:
 *   - rhinobill-claim/constants.js                   — DODD_CODES, PLACE_OF_SERVICE, ...
 *   - rhinobill-claim/utils/dates.js                 — smartParseDate, dayOfWeek, ...
 *   - rhinobill-claim/utils/hcpcsVariants.js         — variant resolution + payer/service helpers
 *   - rhinobill-claim/utils/serviceLine.js           — newServiceLine + UID
 *   - rhinobill-claim/components/SmartDateInput.jsx  — MM/DD/YYYY Day input
 *   - rhinobill-claim/components/ModifierChips.jsx   — ModifierChip + ModifierCell
 *   - rhinobill-claim/components/PatientAutocomplete.jsx
 *   - rhinobill-claim/components/PreviousClaimDialog.jsx
 *   - rhinobill-claim/components/PreviewClaimDialog.jsx
 */

/** Submit button label — picks between Preview / Update & Submit / Submit Claim. */
function submitButtonLabel(isEditMode, showPreview) {
  if (showPreview) return 'Preview Claim';
  if (isEditMode) return 'Update & Submit';
  return 'Submit Claim';
}

const RhinobillClaimPage = () => {
  const navigate = useNavigate();
  const { claimId } = useParams();
  const isEditMode = Boolean(claimId);

  const [patients, setPatients] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // P22 user-pref toggles (persisted in localStorage so the preference
  // sticks per biller-session)
  const readToggle = (k, def) => {
    try { const v = localStorage.getItem(`rhinobill-toggle-${k}`); return v === null ? def : v === 'true'; }
    catch { return def; }
  };
  const writeToggle = (k, v) => {
    try { localStorage.setItem(`rhinobill-toggle-${k}`, String(v)); }
    catch (error) {
      // localStorage can throw in private-mode Safari or when quota
      // is exceeded — the toggle still works in-memory for the
      // session, we just can't persist it. Log for diagnostics.
      logger.warn('Failed to persist toggle to localStorage', { key: k, error });
    }
  };
  const [useUnits, setUseUnits] = useState(readToggle('use-units', true));
  const [showPreview, setShowPreview] = useState(readToggle('show-preview', true));
  const [runEligibilityToggle, setRunEligibilityToggle] = useState(readToggle('run-eligibility', false));
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPayerKey, setSelectedPayerKey] = useState('');
  const [form, setForm] = useState({
    payer_type: '',
    payer_program: '',
    service_code: '',
    // P26 (Feb 25, 2026): HCPCS variant key when the service has flavor
    // variants (e.g., "T1000-RN" / "T1000-LPN"). Empty for plain HCPCS.
    hcpcs_variant: '',
    diagnosis_code: '',
    place_of_service: '12',
    rendering_provider_npi: '',
    rendering_provider_first_name: '',
    rendering_provider_last_name: '',
    referring_provider_npi: '',
    referring_provider_first_name: '',
    referring_provider_last_name: '',
    notes: '',
    prior_authorization: '',
    patient_responsibility: 0,
  });
  const [lines, setLines] = useState([newServiceLine()]);

  // Modifier catalog + per-code chip list — fetched when service code changes.
  const [modifierAvailable, setModifierAvailable] = useState([]);
  const [modifierCatalog, setModifierCatalog] = useState(FALLBACK_MODIFIER_CATALOG);
  const [modifierRefOpen, setModifierRefOpen] = useState(false);

  // Eligibility result of the most recent on-demand 270/271 check
  const [eligibilityRunning, setEligibilityRunning] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);

  // View previous claims
  const [previousClaims, setPreviousClaims] = useState([]);
  const [previousOpen, setPreviousOpen] = useState(false);

  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // ── Initial data load ─────────────────────────────────────────
  const loadInitial = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([patientsApi.getAll(), codesApi.getAll()]);
      setPatients(pRes.data);
      setCodes(cRes.data);
    } catch (e) {
      logger.error('Failed to load patients/codes', e);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadInitial(); }, [loadInitial]);

  // ── Edit-mode hydration ───────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: claim } = await claimsApi.getById(claimId);
        if (cancelled) return;
        if (claim.status !== 'Draft') {
          toast.error('Only Draft claims can be edited.');
          navigate('/claims');
          return;
        }
        const pt = patients.find((p) => p.id === claim.patient_id);
        if (pt) setSelectedPatient(pt);
        const payerKey = `${claim.payer_type || ''}|${claim.payer_program || ''}`;
        setSelectedPayerKey(payerKey);
        setForm({
          payer_type: claim.payer_type || '',
          payer_program: claim.payer_program || '',
          service_code: claim.service_code || '',
          hcpcs_variant: claim.hcpcs_variant || '',
          diagnosis_code: claim.diagnosis_code || '',
          place_of_service: claim.place_of_service || '12',
          rendering_provider_npi: claim.rendering_provider_npi || '',
          rendering_provider_first_name: claim.rendering_provider_first_name || '',
          rendering_provider_last_name: claim.rendering_provider_last_name || '',
          referring_provider_npi: claim.referring_provider_npi || '',
          referring_provider_first_name: claim.referring_provider_first_name || '',
          referring_provider_last_name: claim.referring_provider_last_name || '',
          notes: claim.notes || '',
          prior_authorization: claim.prior_authorization || '',
          patient_responsibility: claim.patient_responsibility || 0,
        });
        if (claim.service_lines && claim.service_lines.length) {
          setLines(claim.service_lines.map((sl) => {
            const mods = [sl.modifier_1, sl.modifier_2, sl.modifier_3, sl.modifier_4]
              .filter((m) => m && String(m).trim() !== '');
            return newServiceLine({
              service_date: sl.service_date || todayISO(),
              units: sl.units || 0,
              modifiers: mods,
            });
          }));
        } else {
          setLines([newServiceLine({ service_date: claim.service_date || todayISO(), units: claim.units || 0 })]);
        }
      } catch (e) {
        logger.error('Failed to hydrate draft', e);
        toast.error('Could not load this draft.');
        navigate('/claims');
      }
    })();
    return () => { cancelled = true; };
  }, [claimId, isEditMode, loading, patients, navigate]);

  // ── Patient select → autofill payer/service/dx/NPI ────────────
  const handlePatientSelect = useCallback((patient) => {
    setSelectedPatient(patient);
    setEligibilityResult(null);
    setPreviousClaims([]);
    if (!patient) {
      setSelectedPayerKey('');
      setForm((f) => ({ ...f, payer_type: '', payer_program: '', service_code: '' }));
      return;
    }
    const payers = distinctPayerOptions(patient);
    const primaryPayer = payers[0] || { key: '', payer_type: '', payer_program: '' };
    setSelectedPayerKey(primaryPayer.key);
    const svcsForPrimary = servicesForPayer(patient, primaryPayer.key);
    const primary = svcsForPrimary[0] || { code: '', variant_key: null };
    const primaryCode = primary.code || '';
    const primaryVariant = primary.variant_key || '';
    const physName = (patient.physician_name || '').trim();
    let first = '', last = '';
    if (physName) {
      if (physName.includes(',')) {
        const [l, f] = physName.split(',').map((s) => s.trim());
        last = (l || '').toUpperCase(); first = (f || '').toUpperCase();
      } else {
        const parts = physName.split(/\s+/);
        last = parts[parts.length - 1].toUpperCase();
        first = parts.slice(0, -1).join(' ').toUpperCase();
      }
    }
    setForm((f) => ({
      ...f,
      payer_type: primaryPayer.payer_type,
      payer_program: primaryPayer.payer_program,
      service_code: primaryCode,
      hcpcs_variant: primaryVariant,
      diagnosis_code: patient.primary_icd10_code || '',
      // P25 (Feb 24, 2026 — ODM E181 rejection feedback): physician = ORP, NOT rendering.
      // Leave rendering_provider_* EMPTY so the 837P builder skips
      // the 2310B loop (agency IS the rendering entity for HH).
      rendering_provider_npi: '',
      rendering_provider_first_name: '',
      rendering_provider_last_name: '',
      referring_provider_npi: patient.physician_npi || '',
      referring_provider_first_name: first,
      referring_provider_last_name: last,
    }));
  }, []);

  // ── When payer changes, refresh service to first matching code ──
  const handlePayerChange = (newKey) => {
    setSelectedPayerKey(newKey);
    const [pt, pp] = newKey.split('|');
    const svcs = servicesForPayer(selectedPatient, newKey);
    const first = svcs[0] || { code: '', variant_key: null };
    setForm((f) => ({
      ...f,
      payer_type: pt,
      payer_program: pp,
      service_code: first.code || f.service_code,
      hcpcs_variant: first.variant_key || '',
    }));
  };

  // ── When the user picks a service from the dropdown ─────────────
  const handleServiceChange = (selectionKey) => {
    // selectionKey is either a bare HCPCS ("G0299") or a variant
    // key ("T1000-RN"). Resolve to the underlying code + variant.
    const variants = Object.values(HCPCS_VARIANTS_FRONTEND).flat();
    const matchVariant = variants.find((v) => v.key === selectionKey);
    if (matchVariant) {
      const code = resolveHcpcsFromVariant(selectionKey);
      setForm((f) => ({ ...f, service_code: code, hcpcs_variant: selectionKey }));
    } else {
      setForm((f) => ({ ...f, service_code: selectionKey, hcpcs_variant: '' }));
    }
  };

  // ── Load previous claims when patient + code combo changes ────
  useEffect(() => {
    if (!selectedPatient || !form.service_code) { setPreviousClaims([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${base}/api/claims/similar`, {
          params: { patient_id: selectedPatient.id, service_code: form.service_code, limit: 2 },
          withCredentials: true,
        });
        if (!cancelled) setPreviousClaims(res.data?.claims || []);
      } catch (e) {
        if (!cancelled) setPreviousClaims([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPatient, form.service_code]);

  // P28 (Feb 26, 2026) — Cache modifier-catalog responses by service-key
  // for the page lifetime. The claim form lets billers swap services on
  // the fly (chronic conditions often bill multiple codes for the same
  // patient), and re-fetching the catalog every time wastes ~120ms +
  // a round-trip per swap. Keys are the variant-or-code string so
  // T1000-RN vs T1000-LPN resolve to different cache entries.
  const modifierCacheRef = useRef({});

  // ── Load per-code modifier catalog whenever the service code/variant changes ─
  useEffect(() => {
    if (!form.service_code) { setModifierAvailable([]); return; }
    const codeQuery = form.hcpcs_variant || form.service_code;
    // Cache hit → skip the network round-trip.
    const cached = modifierCacheRef.current[codeQuery];
    if (cached) {
      setModifierAvailable(cached.available || []);
      if (cached.byCode) setModifierCatalog(cached.byCode);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        // Prefer the variant key (T1000-RN) when set so the backend returns
        // the variant-specific chip list (TD always-on for RN, TE for LPN).
        const res = await axios.get(`${base}/api/claims/modifiers`, {
          params: { service_code: codeQuery },
          withCredentials: true,
        });
        if (cancelled) return;
        const available = res.data?.available || [];
        const byCode = {};
        if (res.data?.catalog) {
          for (const m of res.data.catalog) byCode[m.code] = m;
        }
        modifierCacheRef.current[codeQuery] = { available, byCode };
        setModifierAvailable(available);
        if (res.data?.catalog) setModifierCatalog(byCode);
      } catch (e) {
        if (!cancelled) setModifierAvailable([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.service_code, form.hcpcs_variant]);

  // ── Live preview ──────────────────────────────────────────────
  const codeMeta = useMemo(
    () => codes.find((c) => c.code === form.service_code) || null,
    [codes, form.service_code]
  );

  const linesWithPreview = useMemo(() => {
    const sameDay = {};
    const maxUnits = maxUnitsFor(form.hcpcs_variant || form.service_code);
    const variantAlwaysOn = alwaysApplyFor(form.hcpcs_variant);
    const isAssessment = maxUnits !== null;
    return lines.map((line) => {
      const rawValue = parseFloat(line.units) || 0;
      // P30: Pull the nested ternary into named branches —
      // assessment codes (T1001) lock at maxUnits regardless of the
      // useUnits toggle; otherwise we honor the toggle (units of 15
      // min vs hours-of-care).
      let units;
      if (isAssessment) units = maxUnits;
      else if (useUnits) units = rawValue;
      else units = rawValue * 4;
      const sd = (line.service_date || '').slice(0, 10);
      sameDay[sd] = (sameDay[sd] || 0) + 1;
      const visitNum = sameDay[sd];
      const unitMinutes = codeMeta ? parseInt(codeMeta.unit_minutes || 15, 10) : 15;
      const duration = Math.round((units * unitMinutes) / 60 * 100) / 100;
      // Active modifiers = manual selections + auto rules
      const manual = new Set(isAssessment ? [] : (line.modifiers || []));
      const auto = new Set();
      // Visit-sequence auto modifiers (skip for assessment codes — they're flat).
      if (!isAssessment) {
        if (duration > 12) auto.add('U4');
        else if (visitNum >= 3) auto.add('U3');
        else if (visitNum === 2) auto.add('U2');
        // Variant always-on (TD for T1000-RN, TE for T1000-LPN)
        for (const m of variantAlwaysOn) auto.add(m);
      }
      // Merged set is auto ∪ manual; auto entries are "locked".
      const allMods = new Set([...auto, ...manual]);
      const overLimit = duration > MAX_VISIT_DURATION_HOURS;
      // Charge calc (ODM tiered pricing).
      const rate = codeMeta ? parseFloat(codeMeta.rate || 0) : 0;
      const baseRate = codeMeta ? parseFloat(codeMeta.base_rate || 0) : 0;
      let charge = 0;
      if (codeMeta) {
        const initialHourUnits = Math.floor(60 / unitMinutes);
        if (units <= initialHourUnits) charge = baseRate || (units * rate);
        else charge = baseRate + (units - initialHourUnits) * rate;
        charge = Math.round(charge * 100) / 100;
      }
      return {
        ...line,
        _units: units,
        _visitNum: visitNum,
        _duration: duration,
        _durationHHMM: unitsToHHMM(units, unitMinutes),
        _charge: charge,
        _autoMods: auto,        // locked (set: { 'U2' } or { 'U3' } or { 'U4' })
        _activeMods: allMods,   // visual state (set)
        _overLimit: overLimit,
        _locked: isAssessment,   // P26: T1001 → unit input + chips disabled
      };
    });
  }, [lines, codeMeta, useUnits, form.hcpcs_variant, form.service_code]);

  const hasOverLimitLine = linesWithPreview.some((l) => l._overLimit);

  const totals = useMemo(() => {
    const totalUnits = linesWithPreview.reduce((s, l) => s + (l._units || 0), 0);
    const totalCharge = Math.round(linesWithPreview.reduce((s, l) => s + l._charge, 0) * 100) / 100;
    return { totalUnits, totalCharge };
  }, [linesWithPreview]);

  const netPayout = useMemo(
    () => Math.max(0, totals.totalCharge - (parseFloat(form.patient_responsibility) || 0)),
    [totals.totalCharge, form.patient_responsibility]
  );

  // ── Multi-line CRUD ───────────────────────────────────────────
  const addLine = () => {
    const lastDate = lines.length > 0 ? lines[lines.length - 1].service_date : todayISO();
    setLines((ls) => [...ls, newServiceLine({ service_date: lastDate, units: useUnits ? 4 : 1 })]);
  };
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  // Toggle a manual modifier on a line. Auto-mods (U2/U3/U4) cannot be
  // toggled off — they're locked by the rule engine.
  const toggleLineModifier = (idx, mod) => {
    setLines((ls) => ls.map((l, i) => {
      if (i !== idx) return l;
      const set = new Set(l.modifiers || []);
      if (set.has(mod)) set.delete(mod); else set.add(mod);
      return { ...l, modifiers: Array.from(set) };
    }));
  };

  // Auto-add a new line whenever a service date is committed on the
  // LAST row (matches Rhinobill UX). Skips if the user already added
  // a new line manually.
  const handleDateCommit = (idx) => {
    if (idx === lines.length - 1) {
      // Defer to next tick so the date state propagates first.
      setTimeout(() => addLine(), 0);
    }
  };

  // ── Run Eligibility on demand ─────────────────────────────────
  const runEligibility = async () => {
    if (!selectedPatient) { toast.error('Pick a patient first.'); return; }
    setEligibilityRunning(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await axios.post(
        `${base}/api/patients/${selectedPatient.id}/check-eligibility`,
        {},
        { withCredentials: true },
      );
      setEligibilityResult(res.data);
      toast.success('Eligibility check complete');
    } catch (e) {
      logger.error('Eligibility check failed', e);
      toast.error(e.response?.data?.detail || 'Eligibility check failed');
    } finally {
      setEligibilityRunning(false);
    }
  };

  // ── Recycle a previous claim ─────────────────────────────────
  const recyclePreviousClaim = (prev) => {
    if (!prev) return;
    setForm((f) => ({
      ...f,
      service_code: prev.service_code || f.service_code,
      diagnosis_code: prev.diagnosis_code || f.diagnosis_code,
      place_of_service: prev.place_of_service || f.place_of_service,
      prior_authorization: prev.prior_authorization_number || f.prior_authorization,
      patient_responsibility: prev.patient_responsibility || 0,
    }));
    // Copy service lines (only keep date + units; user adjusts dates)
    if (prev.service_lines && prev.service_lines.length) {
      setLines(prev.service_lines.map((sl) => newServiceLine({
        service_date: todayISO(),  // new claim → today; user can edit
        units: sl.units || 0,
      })));
    } else if (prev.units) {
      setLines([newServiceLine({ service_date: todayISO(), units: prev.units })]);
    }
    setPreviousOpen(false);
    toast.success(`Recycled ${prev.claim_number}`);
  };

  // ── Save / Submit ─────────────────────────────────────────────
  const buildPayload = () => ({
    patient_id: selectedPatient?.id || '',
    service_date: lines[0]?.service_date || todayISO(),
    service_code: form.service_code,
    hcpcs_variant: form.hcpcs_variant || null,
    units: totals.totalUnits,
    diagnosis_code: form.diagnosis_code,
    place_of_service: form.place_of_service,
    payer_type: form.payer_type || 'ODM',
    payer_program: form.payer_program,
    rendering_provider_npi: form.rendering_provider_npi,
    rendering_provider_first_name: form.rendering_provider_first_name,
    rendering_provider_last_name: form.rendering_provider_last_name,
    referring_provider_npi: form.referring_provider_npi,
    referring_provider_first_name: form.referring_provider_first_name,
    referring_provider_last_name: form.referring_provider_last_name,
    notes: form.notes,
    prior_authorization: form.prior_authorization,
    estimated_reimbursement: totals.totalCharge,
    patient_responsibility: parseFloat(form.patient_responsibility) || 0,
    service_lines: linesWithPreview.map((l) => {
      // Serialize the active modifier set into modifier_1..modifier_4
      // slots. Auto-applied first (so the backend sees the canonical
      // visit-sequence/duration modifier in slot 1), then manual.
      const ordered = [
        ...Array.from(l._autoMods),
        ...(l.modifiers || []).filter((m) => !l._autoMods.has(m)),
      ];
      // P30: strip the client-side `_uid` we use for React reconciliation —
      // the backend Pydantic model doesn't know about it.
      return {
        service_date: l.service_date,
        units: l._units,
        hcpcs_variant: form.hcpcs_variant || null,
        modifier_1: ordered[0] || null,
        modifier_2: ordered[1] || null,
        modifier_3: ordered[2] || null,
        modifier_4: ordered[3] || null,
      };
    }),
    _patientName: selectedPatient ? `${selectedPatient.last_name}, ${selectedPatient.first_name}` : '',
  });

  const canSave = () =>
    selectedPatient && form.service_code && lines.length > 0 &&
    lines.every((l) => l.service_date && parseFloat(l.units) > 0) &&
    !hasOverLimitLine;

  const persist = async (payload, alsoSubmit) => {
    const cleanedPayload = { ...payload };
    delete cleanedPayload._patientName;
    let id = claimId;
    if (isEditMode) {
      await claimsApi.update(claimId, cleanedPayload);
    } else {
      const { data } = await claimsApi.create(cleanedPayload);
      id = data.id;
    }
    if (alsoSubmit && id) {
      await claimsApi.submit(id);
      toast.success(isEditMode ? 'Claim updated and submitted!' : 'Claim created and submitted!');
    } else {
      toast.success(isEditMode ? 'Draft updated' : 'Claim saved as draft');
    }
    navigate('/claims');
  };

  const handleSubmitClick = async () => {
    if (!canSave()) { toast.error('Please pick a patient, a service code, and at least one line with units > 0.'); return; }
    const payload = buildPayload();
    if (showPreview) {
      setPendingPayload(payload);
      setPreviewOpen(true);
    } else {
      setSubmitting(true);
      try { await persist(payload, true); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed to save claim'); }
      finally { setSubmitting(false); }
    }
  };

  const handlePreviewConfirm = async () => {
    if (!pendingPayload) return;
    setSubmitting(true);
    try { await persist(pendingPayload, true); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save claim'); }
    finally { setSubmitting(false); setPreviewOpen(false); setPendingPayload(null); }
  };

  const handleSaveDraft = async () => {
    if (!canSave()) { toast.error('Please pick a patient, a service code, and at least one line with units > 0.'); return; }
    setSubmitting(true);
    try { await persist(buildPayload(), false); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save claim'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const payerOptions = selectedPatient ? distinctPayerOptions(selectedPatient) : [];
  const serviceOptions = selectedPatient ? servicesForPayer(selectedPatient, selectedPayerKey) : [];

  return (
    <div className="max-w-5xl mx-auto animate-fade-in" data-testid="rhinobill-claim-page">
      <div className="mb-6">
        <Button variant="ghost" className="mb-4" onClick={() => navigate('/claims')} data-testid="back-to-claims-btn">
          <ArrowLeft size={18} className="mr-2" /> Back to Claims
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }} data-testid="page-title">
              {isEditMode ? 'Edit Draft Claim' : 'New Claim'}
            </h1>
            <p className="text-slate-500 mt-1">
              Start typing a patient&apos;s name or Medicaid ID. Everything else auto-fills.
            </p>
          </div>
          <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
            <PopoverTrigger asChild>
              <button
                className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-indigo-700"
                aria-label="Settings"
                data-testid="settings-popover-trigger"
              >
                <Gear size={22} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end" data-testid="settings-popover">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="toggle-units" className="text-sm font-medium">Use Units</Label>
                    <Switch
                      id="toggle-units"
                      checked={useUnits}
                      onCheckedChange={(v) => { setUseUnits(v); writeToggle('use-units', v); }}
                      data-testid="toggle-use-units"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {useUnits ? '1 unit = 15 min' : 'Enter visits in hours (1 hr = 4 units)'}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="toggle-preview" className="text-sm font-medium">Show Preview Before Saving</Label>
                  <Switch
                    id="toggle-preview"
                    checked={showPreview}
                    onCheckedChange={(v) => { setShowPreview(v); writeToggle('show-preview', v); }}
                    data-testid="toggle-show-preview"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="toggle-eligibility" className="text-sm font-medium">Run Eligibility</Label>
                  <Switch
                    id="toggle-eligibility"
                    checked={runEligibilityToggle}
                    onCheckedChange={(v) => { setRunEligibilityToggle(v); writeToggle('run-eligibility', v); }}
                    data-testid="toggle-run-eligibility"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  When on, a “Run Eligibility” button appears so you can verify 270/271 status before submitting.
                </p>
                <Button
                  variant="ghost"
                  className="w-full text-indigo-700"
                  onClick={() => setSettingsOpen(false)}
                  data-testid="settings-close-btn"
                >
                  Close
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Patient + Payer + Service ─────────────────────────── */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>Enter Patient <span className="text-red-500">*</span></Label>
              <PatientAutocomplete patients={patients} selected={selectedPatient} onSelect={handlePatientSelect} disabled={isEditMode} />
              {selectedPatient ? (
                <p className="text-xs text-slate-500 mt-1" data-testid="patient-summary">
                  MID {selectedPatient.medicaid_id ? `••${String(selectedPatient.medicaid_id).slice(-4)}` : '—'} ·
                  DOB {selectedPatient.date_of_birth || '?'} ·
                  County: {selectedPatient.county || 'unset'} ·
                  {selectedPatient.pnm_verified ? ' PNM ✓' : ' PNM unverified'}
                </p>
              ) : null}
            </div>
            <div>
              <Label>Insurance Plan</Label>
              <Input
                value={selectedPatient ? friendlyPayerDisplay(selectedPatient) : ''}
                readOnly
                className="bg-slate-50 cursor-not-allowed"
                placeholder="Select a patient to populate…"
                data-testid="insurance-plan-display"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="payer-select">Payer <span className="text-red-500">*</span></Label>
              <Select value={selectedPayerKey} onValueChange={handlePayerChange} disabled={!selectedPatient}>
                <SelectTrigger id="payer-select" data-testid="payer-select">
                  <SelectValue placeholder={selectedPatient ? 'Select Payer' : '— Pick a patient first —'} />
                </SelectTrigger>
                <SelectContent>
                  {payerOptions.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`payer-option-${p.key.replace('|', '-')}`}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="service-select">Service <span className="text-red-500">*</span></Label>
              <Select
                value={form.hcpcs_variant || form.service_code}
                onValueChange={handleServiceChange}
                disabled={!selectedPatient}
              >
                <SelectTrigger id="service-select" data-testid="service-select">
                  <SelectValue placeholder={selectedPatient ? 'Select Service' : '— Pick a patient first —'} />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {(serviceOptions.length ? serviceOptions : codes.map((c) => ({ code: c.code, variant_key: null, label: null })))
                    .filter((s) => !!s.code)
                    .map((s) => {
                      const valueKey = s.variant_key || s.code;
                      const meta = codes.find((c) => c.code === s.code);
                      const label = s.label
                        ? s.label
                        : `${s.code} — ${meta?.description?.slice(0, 40) || ''}`;
                      return (
                        <SelectItem
                          key={valueKey}
                          value={valueKey}
                          data-testid={`service-option-${valueKey}`}
                        >
                          {label}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              {codeMeta ? (
                <p className="text-xs text-slate-500 mt-1">${codeMeta.base_rate || 0} base + ${codeMeta.rate || 0}/{codeMeta.unit_minutes || 15}min</p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="patient-responsibility">Patient Responsibility</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="patient-responsibility"
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-6"
                  value={form.patient_responsibility}
                  onChange={(e) => setForm((f) => ({ ...f, patient_responsibility: e.target.value }))}
                  data-testid="patient-responsibility-input"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Spend-down owed by the patient — deducted from total.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="diagnosis-code">Primary Dx (ICD-10)</Label>
              <Input
                id="diagnosis-code"
                value={form.diagnosis_code}
                onChange={(e) => setForm((f) => ({ ...f, diagnosis_code: e.target.value.toUpperCase() }))}
                placeholder="e.g. M62.81"
                data-testid="diagnosis-code-input"
              />
              {selectedPatient && !form.diagnosis_code ? (
                <p className="text-xs text-red-600 mt-1" data-testid="dx-missing-warning">Patient profile has no primary ICD-10 — please enter one.</p>
              ) : (
                <p className="text-xs text-slate-400 mt-1">Auto-filled from patient profile.</p>
              )}
            </div>
            <div>
              <Label htmlFor="place-of-service">Place of Service</Label>
              <Select value={form.place_of_service} onValueChange={(v) => setForm((f) => ({ ...f, place_of_service: v }))}>
                <SelectTrigger id="place-of-service" data-testid="pos-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLACE_OF_SERVICE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`pos-option-${opt.value}`}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end gap-2">
              {runEligibilityToggle ? (
                <Button
                  variant="outline"
                  onClick={runEligibility}
                  disabled={!selectedPatient || eligibilityRunning}
                  className="text-indigo-800"
                  data-testid="run-eligibility-btn"
                >
                  <ShieldCheck size={16} className="mr-2" />
                  {eligibilityRunning ? 'Running…' : 'Run Eligibility'}
                </Button>
              ) : null}
            </div>
          </div>
          {eligibilityResult ? (
            <div
              className={`mt-1 p-3 rounded-md text-xs border ${eligibilityResult.eligible ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}
              data-testid="eligibility-result"
            >
              <strong>{eligibilityResult.eligible ? 'Eligible ✓' : 'Eligibility check returned issues'}</strong>
              {eligibilityResult.payer ? <> · Payer: {eligibilityResult.payer}</> : null}
              {eligibilityResult.plan_type ? <> · Plan: {eligibilityResult.plan_type}</> : null}
              {eligibilityResult.message ? <div className="mt-1">{eligibilityResult.message}</div> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* ── View Previous Claim link ──────────────────────────── */}
      {selectedPatient ? (
        <div className="mb-4 text-center">
          <button
            type="button"
            onClick={() => setPreviousOpen(true)}
            className="text-sm text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
            data-testid="view-previous-claim-link"
          >
            <ClockCounterClockwise size={16} />
            View/Recycle Previous Claim {previousClaims.length ? `(${previousClaims.length})` : '(none yet)'}
          </button>
        </div>
      ) : null}

      {/* ── DODD CODB breadcrumb (only shown for DODD codes) ─────── */}
      {selectedPatient && isDoddCode(form.service_code) ? (() => {
        // P28 (Feb 26, 2026): CODB tier follows the *patient's* county,
        // not the agency's HQ. Surfaces the resolved tier here so the
        // biller can confirm DODD pricing before submit.
        const county = selectedPatient.county || '';
        const tierFromCounty = county ? countyToCodbTier(county) : null;
        // Patient-level override (manual codb_tier set on patient profile)
        // wins over county derivation.
        const patientOverride = selectedPatient.codb_tier;
        const tier = patientOverride || tierFromCounty || 1;
        // P30: replaced nested ternary with explicit branches.
        let source;
        if (patientOverride) source = 'manual patient override';
        else if (tierFromCounty) source = `patient county (${county})`;
        else source = 'default — county unset';
        const isWarn = !patientOverride && !tierFromCounty;
        const wrapperClass = isWarn
          ? 'bg-amber-50 border-amber-200 text-amber-900'
          : 'bg-indigo-50 border-indigo-200 text-indigo-900';
        return (
          <div
            className={`mb-4 p-3 rounded-md text-sm flex items-start gap-2 border ${wrapperClass}`}
            data-testid="dodd-codb-breadcrumb"
          >
            <ShieldCheck size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">
                DODD pricing · CODB Tier {tier}
                <span className="ml-2 text-xs font-mono opacity-70" data-testid="dodd-codb-source">
                  {source}
                </span>
              </div>
              <div className="text-xs mt-1">
                {isWarn ? (
                  <>
                    <strong>No county on this patient&apos;s profile.</strong> Defaulting to Tier 1
                    — the lowest DODD rate.{' '}
                    <a
                      href={`/patients?edit=${selectedPatient.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      data-testid="fix-patient-county-link"
                    >
                      Set the patient&apos;s county →
                    </a>
                  </>
                ) : (
                  <>
                    Per OAC 5123-9-14 Appendix C, every DODD claim is priced
                    against the rate matrix row for the patient&apos;s county.
                    Different patients in different counties bill at different
                    rates within the same submission batch.
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })() : null}

      {/* ── Service lines ───────────────────────────────────── */}
      <Card className="border-slate-200 mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Service Lines</h2>
              <p className="text-sm text-slate-500">
                Type a date (e.g. <code className="bg-slate-100 px-1 rounded text-xs">15</code>, <code className="bg-slate-100 px-1 rounded text-xs">02/15</code>, or <code className="bg-slate-100 px-1 rounded text-xs">02/15/2026</code>) and a new line appears automatically. Modifier chips light up based on the rule book.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Popover open={modifierRefOpen} onOpenChange={setModifierRefOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-indigo-700" data-testid="modifier-reference-btn">
                    <Info size={14} className="mr-1" /> Modifier Reference
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-96" align="end" data-testid="modifier-reference-popover">
                  <div className="space-y-2 text-xs">
                    <div className="font-semibold text-slate-900 text-sm">Modifier Reference</div>
                    <table className="w-full">
                      <tbody>
                        {Object.values(modifierCatalog).map((m) => (
                          <tr key={m.code} className="border-b border-slate-100 last:border-b-0">
                            <td className="py-1 pr-2 font-mono font-semibold text-indigo-800 align-top">{m.code}</td>
                            <td className="py-1 text-slate-700">{m.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={addLine} variant="outline" size="sm" data-testid="add-line-btn">
                <Plus size={16} className="mr-2" /> Add line
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="service-lines-table">
              <thead>
                <tr className="border-b text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left py-2 px-2 w-12">#</th>
                  <th className="text-left py-2 px-2 w-44">Service Date</th>
                  <th className="text-left py-2 px-2 w-32">{useUnits ? 'Units' : 'Hours'}</th>
                  <th className="text-left py-2 px-2 w-20">Visit #</th>
                  <th className="text-left py-2 px-2">Modifiers</th>
                  <th className="text-right py-2 px-2 w-32">Charge</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {linesWithPreview.map((l, idx) => (
                  <tr
                    key={l._uid || idx}
                    className={`border-b ${l._overLimit ? 'bg-red-50' : ''}`}
                    data-testid={`service-line-row-${idx}`}
                  >
                    <td className="py-2 px-2 text-slate-500">{idx + 1}</td>
                    <td className="py-2 px-2">
                      <SmartDateInput
                        value={l.service_date}
                        onChange={(v) => updateLine(idx, { service_date: v })}
                        onCommit={() => handleDateCommit(idx)}
                        testId={`line-date-${idx}`}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step={useUnits ? '1' : '0.25'}
                          value={l._locked ? l._units : l.units}
                          disabled={l._locked}
                          onChange={(e) => updateLine(idx, { units: e.target.value })}
                          className={`pr-16 ${l._locked ? 'bg-slate-50 cursor-not-allowed' : ''}`}
                          data-testid={`line-units-${idx}`}
                        />
                        <span
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-mono text-slate-400 pointer-events-none tabular-nums"
                          data-testid={`line-duration-${idx}`}
                        >
                          {l._durationHHMM}
                        </span>
                      </div>
                      {l._locked ? (
                        <p className="text-[10px] text-slate-400 italic mt-1" data-testid={`line-locked-hint-${idx}`}>
                          Flat-rate (1 unit per visit)
                        </p>
                      ) : null}
                    </td>
                    <td className="py-2 px-2 text-slate-700" data-testid={`line-visit-num-${idx}`}>{l._visitNum}</td>
                    <td className="py-2 px-2">
                      <ModifierCell
                        line={l}
                        idx={idx}
                        modifierAvailable={modifierAvailable}
                        serviceCode={form.service_code}
                        onToggle={toggleLineModifier}
                      />
                      {l._overLimit ? (
                        <p className="text-xs text-red-700 font-medium mt-2" data-testid={`line-${idx}-over-limit`}>
                          ⚠ {l._duration} hrs exceeds the {MAX_VISIT_DURATION_HOURS}-hour ODM cap (OAC 5160-12-05). Split into multiple visits.
                        </p>
                      ) : null}
                    </td>
                    <td className="py-2 px-2 text-right font-mono" data-testid={`line-charge-${idx}`}>${l._charge.toFixed(2)}</td>
                    <td className="py-2 px-2">
                      {lines.length > 1 && (
                        <button onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700"
                          aria-label="Remove line" data-testid={`remove-line-${idx}`}>
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold text-slate-800">
                  <td colSpan={2} className="py-3 px-2">Total ({lines.length} {lines.length === 1 ? 'visit' : 'visits'})</td>
                  <td className="py-3 px-2" data-testid="total-units">{totals.totalUnits} units</td>
                  <td colSpan={2}></td>
                  <td className="py-3 px-2 text-right font-mono" data-testid="total-charge">${totals.totalCharge.toFixed(2)}</td>
                  <td></td>
                </tr>
                {parseFloat(form.patient_responsibility) > 0 ? (
                  <>
                    <tr className="text-amber-700">
                      <td colSpan={4} className="py-1 px-2 text-right text-xs">Less patient responsibility</td>
                      <td className="py-1 px-2 text-right font-mono text-xs" data-testid="patient-resp-display">– ${Number(form.patient_responsibility).toFixed(2)}</td>
                      <td></td>
                    </tr>
                    <tr className="font-semibold text-indigo-900 border-t">
                      <td colSpan={4} className="py-2 px-2 text-right">Net payout</td>
                      <td className="py-2 px-2 text-right font-mono" data-testid="net-payout">${netPayout.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </>
                ) : null}
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Action bar ───────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate('/claims')} data-testid="cancel-btn">Cancel</Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleSaveDraft} disabled={!canSave() || submitting} data-testid="save-draft-btn">
            {isEditMode ? 'Save Changes' : 'Save as Draft'}
          </Button>
          <Button onClick={handleSubmitClick} disabled={!canSave() || submitting} className="bg-indigo-900 hover:bg-indigo-800" data-testid="submit-claim-btn">
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle size={18} className="mr-2" />
                {submitButtonLabel(isEditMode, showPreview)}
                <ArrowRight size={18} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      <PreviousClaimDialog
        open={previousOpen}
        onClose={setPreviousOpen}
        claims={previousClaims.map((c) => ({ ...c, _recycle: () => recyclePreviousClaim(c) }))}
      />
      <PreviewClaimDialog
        open={previewOpen}
        onClose={(v) => { if (!v) setPreviewOpen(false); }}
        payload={pendingPayload}
        totals={totals}
        onConfirm={handlePreviewConfirm}
        submitting={submitting}
      />
    </div>
  );
};

export default RhinobillClaimPage;
